import React, { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ClipboardCheck,
  Settings,
  Tag,
  DollarSign,
  Database,
  Search,
  Plus,
  Trash2,
  Info,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  SlidersHorizontal,
  Undo2,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
import type { 
  PricebookChargeType,
  PricebookEditorViewModel, 
  PricebookOfferPriceRowViewModel,
  PricebookOfferFeeRowViewModel,
  PricebookFeePriceRowViewModel,
  PricebookDirectoryRowViewModel,
  PricebookReviewChangeItemViewModel,
} from "../../contracts/commerce-admin";
import {
  HorizontalStack,
  VerticalStack,
  DescriptionText,
  Surface,
  SectionHeading,
  Field,
  CurrencyInput,
  PercentageInput,
  inputStyles,
  labelStyles
} from "../blocks";
import { FieldHelpTooltip, leaseFieldHelpText } from "./leaseFieldHelp";

const equipmentLeaseRecurringOfferType = "equipment_lease_recurring";

function createRowId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function currencySymbolForCode(currency?: string | null) {
  switch ((currency || "USD").toUpperCase()) {
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    default:
      return "$";
  }
}

export function formatBasisPointsAsPercent(bps?: number | null): string {
  if (bps === null || bps === undefined) {
    return "";
  }
  return (bps / 100).toString();
}

export function parsePercentAsBasisPoints(value: string): number | null {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return Math.round(parsed * 100);
}

function pricebookFieldId(field: string) {
  return `pricebook-${field.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function LeaseFieldLabel({ label, helpKey, field }: { label: string; helpKey: keyof typeof leaseFieldHelpText; field: string }) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      <label htmlFor={pricebookFieldId(field)} className={cn(labelStyles, "mb-0")}>{label}</label>
      <FieldHelpTooltip label={label} text={leaseFieldHelpText[helpKey]} />
    </div>
  );
}

// The viewmodel stores basis points; operators type normal percentages. Local
// display state keeps partial input like "8." editable between keystrokes.
function LeaseRatePercentInput({
  valueBps,
  onChangeBps,
  field,
  invalid,
}: {
  valueBps?: number | null;
  onChangeBps: (bps: number | null) => void;
  field?: string;
  invalid?: boolean;
}) {
  const [display, setDisplay] = useState(() => formatBasisPointsAsPercent(valueBps));

  React.useEffect(() => {
    if ((valueBps ?? null) !== parsePercentAsBasisPoints(display)) {
      setDisplay(formatBasisPointsAsPercent(valueBps));
    }
  }, [valueBps]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PercentageInput
      id={field ? pricebookFieldId(field) : undefined}
      data-pricebook-field={field}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid && field ? fieldErrorId(field) : undefined}
      value={display}
      placeholder="8.25"
      onChange={e => {
        setDisplay(e.target.value);
        onChangeBps(parsePercentAsBasisPoints(e.target.value));
      }}
    />
  );
}

function fieldErrorId(field: string) {
  return `pricebook-${field.replace(/[^a-zA-Z0-9_-]/g, "-")}-error`;
}

function ValidationError({ message, field }: { message?: string | null; field?: string }) {
  if (!message) return null;
  return <p id={field ? fieldErrorId(field) : undefined} className="mt-1.5 text-[11px] font-semibold text-destructive">{message}</p>;
}

const lifecycleLabels = {
  saved: "Saved",
  draft: "Draft",
  modified: "Modified",
  needs_attention: "Needs attention",
  marked_for_removal: "Marked for removal",
} as const;

function LifecycleBadge({ lifecycle }: { lifecycle: PricebookDirectoryRowViewModel["lifecycle"] }) {
  const blocking = lifecycle === "needs_attention" || lifecycle === "marked_for_removal";
  const changed = lifecycle === "draft" || lifecycle === "modified";
  return (
    <span className={cn(
      "inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold",
      blocking && "bg-destructive/10 text-destructive",
      changed && "bg-primary/10 text-primary",
      lifecycle === "saved" && "bg-muted text-muted-foreground",
    )}>
      {lifecycleLabels[lifecycle]}
    </span>
  );
}

function LeaseSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="sm:col-span-2 xl:col-span-3">
      <p className="pb-2 pt-3 text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 border-b border-border/40">
        {title}
      </p>
      <div className="grid grid-cols-1 gap-4 pt-4 sm:grid-cols-2 xl:grid-cols-3">
        {children}
      </div>
    </div>
  );
}

export function PricebookDesignerWorkspace({ viewModel, className }: {
  viewModel: PricebookEditorViewModel;
  className?: string;
}) {
  const {
    isOpen, 
    state,
    saveActionLabel,
    value,
    statusOptions,
    currencyOptions,
    catalogOptions,
    sellingTenantOptions,
    pricingStrategyOptions,
    chargeTypeOptions,
    billingPeriodOptions,
    offerTargetOptions,
    feeTargetOptions,
    validationErrors,
    validationIssues,
    dirtyCount,
    activeDestination,
    activePriceScope,
    directoryQuery,
    directoryCounts,
    productGroups,
    selectedInspector,
    controlledFocusTarget,
    reviewChangeGroups,
    selectedPriceRowId,
    onChange,
    onDestinationChange,
    onPriceScopeChange,
    onDirectoryQueryChange,
    onToggleProductExpanded,
    onMarkPriceRowForRemoval,
    onDiscardPriceDraft,
    onUndoPriceRowRemoval,
    onControlledFocusHandled,
    onOpenOfferPrice,
    onSelectPriceRow,
    onSelectValidationIssue,
    onSave,
    onClose,
  } = viewModel;
  const workspaceRef = useRef<HTMLDivElement>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const pendingReviewFieldRef = useRef<string | null>(null);

  const isSaving = state === "saving";
  const isLoading = state === "loading";
  const fieldAccessibility = (field: string) => ({
    id: pricebookFieldId(field),
    "data-pricebook-field": field,
    "aria-invalid": validationErrors[field] ? true : undefined,
    "aria-describedby": validationErrors[field] ? fieldErrorId(field) : undefined,
  });

  const patchOfferRow = (index: number, patch: Partial<(typeof value.offerPrices)[number]>) => {
    onChange({
      offerPrices: value.offerPrices.map((row, i) => i === index ? { ...row, ...patch } : row)
    });
  };

  const patchOfferLeasePricing = (
    index: number,
    patch: NonNullable<PricebookOfferPriceRowViewModel["leasePricing"]>
  ) => {
    const current = value.offerPrices[index]?.leasePricing || {};
    patchOfferRow(index, { leasePricing: { ...current, ...patch } });
  };

  const patchOfferFeeRow = (offerIndex: number, feeIndex: number, patch: Partial<PricebookOfferFeeRowViewModel>) => {
    const fees = value.offerPrices[offerIndex]?.fees || [];
    patchOfferRow(offerIndex, {
      fees: fees.map((fee, i) => i === feeIndex ? { ...fee, ...patch } : fee),
    });
  };

  const addOfferFeeRow = (offerIndex: number) => {
    const fees = value.offerPrices[offerIndex]?.fees || [];
    patchOfferRow(offerIndex, {
      fees: [
        ...fees,
        {
          rowId: createRowId("of"),
          feeLabel: "",
          feeCode: "",
          chargeType: "one_time",
          amount: "",
          percentage: "",
          currency: value.currency || "USD",
          billingPeriod: "",
          summary: "",
        },
      ],
    });
  };

  const removeOfferFeeRow = (offerIndex: number, feeIndex: number) => {
    const fees = value.offerPrices[offerIndex]?.fees || [];
    patchOfferRow(offerIndex, { fees: fees.filter((_, i) => i !== feeIndex) });
  };

  const patchFeeRow = (index: number, patch: Partial<PricebookFeePriceRowViewModel>) => {
    onChange({
      feePrices: value.feePrices.map((row, i) => i === index ? { ...row, ...patch } : row)
    });
  };

  const removeFeeRow = (index: number) => {
    const removedRowId = value.feePrices[index]?.rowId;
    onChange({
      feePrices: value.feePrices.filter((_, i) => i !== index)
    });
    if (removedRowId && removedRowId === selectedPriceRowId) {
      onSelectPriceRow(null);
    }
  };

  const addFeeRow = (feeId: string) => {
    const target = feeTargetOptions.find(o => o.value === feeId);
    if (!target) return;

    const rowId = createRowId("f");

    onChange({
      feePrices: [
        ...value.feePrices,
        {
          rowId,
          feeId: target.value,
          feeLabel: target.label,
          appliesToLabel: target.description || "",
          chargeType: "one_time",
          amount: "",
          percentage: "",
          currency: value.currency || "USD",
          summary: "",
        }
      ]
    });
    onSelectPriceRow(rowId);
    onPriceScopeChange("catalog_fees");
  };

  const unpricedFeeTargets = feeTargetOptions.filter(target =>
    !value.feePrices.some(row => row.feeId === target.value),
  );
  const selectedOfferEntry = value.offerPrices.findIndex(row => row.rowId === selectedPriceRowId);
  const selectedFeeEntry = value.feePrices.findIndex(row => row.rowId === selectedPriceRowId);

  const productFacetOptions = React.useMemo(
    () => Array.from(new Map(offerTargetOptions.flatMap(option => {
      const productId = typeof option.metadata?.productId === "string" ? option.metadata.productId : "";
      const productLabel = typeof option.metadata?.productLabel === "string" ? option.metadata.productLabel : "";
      return productId && productLabel ? [[productId, productLabel] as const] : [];
    })), ([value, label]) => ({ value, label })).sort((left, right) => left.label.localeCompare(right.label)),
    [offerTargetOptions],
  );
  const offerTypeFacetOptions = React.useMemo(() => {
    const options = new Map<string, string>();
    productGroups.forEach(group => group.rows.forEach(row => options.set(row.offerType, row.offerTypeLabel)));
    offerTargetOptions.forEach(option => {
      const value = typeof option.metadata?.offerType === "string" ? option.metadata.offerType : "";
      if (value && !options.has(value)) options.set(value, value.split("_").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(" "));
    });
    return Array.from(options, ([value, label]) => ({ value, label })).sort((left, right) => left.label.localeCompare(right.label));
  }, [offerTargetOptions, productGroups]);

  useEffect(() => {
    if (!controlledFocusTarget || controlledFocusTarget.destination !== activeDestination) return;
    if (controlledFocusTarget.scope && controlledFocusTarget.scope !== activePriceScope) return;
    const target = Array.from(workspaceRef.current?.querySelectorAll<HTMLElement>("[data-pricebook-field]") || [])
      .find(element => element.dataset.pricebookField === controlledFocusTarget.field);
    if (!target) return;
    target.focus();
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    onControlledFocusHandled(controlledFocusTarget.requestId);
  }, [activeDestination, activePriceScope, controlledFocusTarget, onControlledFocusHandled, selectedPriceRowId]);

  useEffect(() => {
    const pendingReviewField = pendingReviewFieldRef.current;
    if (!pendingReviewField) return;
    const target = Array.from(workspaceRef.current?.querySelectorAll<HTMLElement>("[data-pricebook-field]") || [])
      .find(element => element.dataset.pricebookField === pendingReviewField);
    if (!target) return;
    target.focus();
    target.scrollIntoView({ block: "center", behavior: "smooth" });
    pendingReviewFieldRef.current = null;
  }, [activeDestination, activePriceScope, selectedPriceRowId]);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileFiltersOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileFiltersOpen]);

  const navigateToChange = (item: PricebookReviewChangeItemViewModel) => {
    onDestinationChange(item.destination);
    if (item.scope) onPriceScopeChange(item.scope);
    if (item.rowId) onSelectPriceRow(item.rowId);
    pendingReviewFieldRef.current = item.field || null;
    const issueId = item.id.startsWith("issue:") ? item.id.slice("issue:".length) : item.id;
    const issue = validationIssues.find(candidate => candidate.id === issueId || `issue:${candidate.id}` === item.id);
    if (issue) onSelectValidationIssue(issue.id);
  };

  const renderOfferPriceEditor = (row: PricebookOfferPriceRowViewModel, index: number) => (
    <div key={row.rowId} className="px-4 py-5 sm:px-6">
                      <VerticalStack gap={4}>

                        {validationErrors[`offerPrices.${index}.offerId`] && (
                          <div
                            data-pricebook-field={`offerPrices.${index}.offerId`}
                            tabIndex={-1}
                            aria-invalid="true"
                            aria-describedby={fieldErrorId(`offerPrices.${index}.offerId`)}
                            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
                          >
                            <ValidationError field={`offerPrices.${index}.offerId`} message={validationErrors[`offerPrices.${index}.offerId`]} />
                          </div>
                        )}

                        {row.offerType === equipmentLeaseRecurringOfferType ? (
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                          <LeaseSection title="Internal Accounting">
                            <div>
                                <LeaseFieldLabel label="Purchase Cost Amount" helpKey="purchaseCostAmount" field={`offerPrices.${index}.leasePricing.purchaseCostAmount`} />
                                <CurrencyInput
                                    {...fieldAccessibility(`offerPrices.${index}.leasePricing.purchaseCostAmount`)}
                                    currencySymbol={currencySymbolForCode(row.leasePricing?.purchaseCostCurrency || value.currency)}
                                    value={row.leasePricing?.purchaseCostAmount ?? ""}
                                    onChange={e => patchOfferLeasePricing(index, { purchaseCostAmount: e.target.value || null })}
                                    className={cn(validationErrors[`offerPrices.${index}.leasePricing.purchaseCostAmount`] && 'border-destructive')}
                                />
                                <ValidationError field={`offerPrices.${index}.leasePricing.purchaseCostAmount`} message={validationErrors[`offerPrices.${index}.leasePricing.purchaseCostAmount`]} />
                            </div>
                            <div>
                                <LeaseFieldLabel label="Purchase Cost Currency" helpKey="purchaseCostCurrency" field={`offerPrices.${index}.leasePricing.purchaseCostCurrency`} />
                                <input
                                    {...fieldAccessibility(`offerPrices.${index}.leasePricing.purchaseCostCurrency`)}
                                    value={row.leasePricing?.purchaseCostCurrency || value.currency || ""}
                                    onChange={e => patchOfferLeasePricing(index, { purchaseCostCurrency: e.target.value.toUpperCase() })}
                                    className={cn(inputStyles, validationErrors[`offerPrices.${index}.leasePricing.purchaseCostCurrency`] && 'border-destructive')}
                                />
                                <ValidationError field={`offerPrices.${index}.leasePricing.purchaseCostCurrency`} message={validationErrors[`offerPrices.${index}.leasePricing.purchaseCostCurrency`]} />
                            </div>
                          </LeaseSection>

                          <LeaseSection title="Customer Pricing Basis">
                              <div>
                                  <LeaseFieldLabel label="Intended Sale Price Amount" helpKey="intendedSalePriceAmount" field={`offerPrices.${index}.leasePricing.intendedSalePriceAmount`} />
                                  <CurrencyInput
                                      {...fieldAccessibility(`offerPrices.${index}.leasePricing.intendedSalePriceAmount`)}
                                      currencySymbol={currencySymbolForCode(row.leasePricing?.intendedSalePriceCurrency || value.currency)}
                                      value={row.leasePricing?.intendedSalePriceAmount ?? ""}
                                      onChange={e => patchOfferLeasePricing(index, { intendedSalePriceAmount: e.target.value || null })}
                                      className={cn(validationErrors[`offerPrices.${index}.leasePricing.intendedSalePriceAmount`] && 'border-destructive')}
                                  />
                                  <ValidationError field={`offerPrices.${index}.leasePricing.intendedSalePriceAmount`} message={validationErrors[`offerPrices.${index}.leasePricing.intendedSalePriceAmount`]} />
                              </div>
                              <div>
                                  <LeaseFieldLabel label="Intended Sale Price Currency" helpKey="intendedSalePriceCurrency" field={`offerPrices.${index}.leasePricing.intendedSalePriceCurrency`} />
                                  <input
                                      {...fieldAccessibility(`offerPrices.${index}.leasePricing.intendedSalePriceCurrency`)}
                                      value={row.leasePricing?.intendedSalePriceCurrency || value.currency || ""}
                                      onChange={e => patchOfferLeasePricing(index, { intendedSalePriceCurrency: e.target.value.toUpperCase() })}
                                      className={cn(inputStyles, validationErrors[`offerPrices.${index}.leasePricing.intendedSalePriceCurrency`] && 'border-destructive')}
                                  />
                                  <ValidationError field={`offerPrices.${index}.leasePricing.intendedSalePriceCurrency`} message={validationErrors[`offerPrices.${index}.leasePricing.intendedSalePriceCurrency`]} />
                              </div>
                              <div>
                                  <LeaseFieldLabel label="Interest Rate" helpKey="leaseRateBps" field={`offerPrices.${index}.leasePricing.leaseRateBps`} />
                                  <LeaseRatePercentInput
                                      field={`offerPrices.${index}.leasePricing.leaseRateBps`}
                                      invalid={Boolean(validationErrors[`offerPrices.${index}.leasePricing.leaseRateBps`])}
                                      valueBps={row.leasePricing?.leaseRateBps}
                                      onChangeBps={bps => patchOfferLeasePricing(index, { leaseRateBps: bps })}
                                  />
                                  <ValidationError field={`offerPrices.${index}.leasePricing.leaseRateBps`} message={validationErrors[`offerPrices.${index}.leasePricing.leaseRateBps`]} />
                              </div>
                              <div>
                                  <LeaseFieldLabel label="Minimum Upfront Payment" helpKey="minimumUpfrontPaymentAmount" field={`offerPrices.${index}.leasePricing.minimumUpfrontPaymentAmount`} />
                                  <CurrencyInput
                                      {...fieldAccessibility(`offerPrices.${index}.leasePricing.minimumUpfrontPaymentAmount`)}
                                      currencySymbol={currencySymbolForCode(row.leasePricing?.intendedSalePriceCurrency || value.currency)}
                                      value={row.leasePricing?.minimumUpfrontPaymentAmount ?? ""}
                                      onChange={e => patchOfferLeasePricing(index, { minimumUpfrontPaymentAmount: e.target.value || null })}
                                      className={cn(validationErrors[`offerPrices.${index}.leasePricing.minimumUpfrontPaymentAmount`] && 'border-destructive')}
                                  />
                                  <ValidationError field={`offerPrices.${index}.leasePricing.minimumUpfrontPaymentAmount`} message={validationErrors[`offerPrices.${index}.leasePricing.minimumUpfrontPaymentAmount`]} />
                              </div>
                              <div>
                                  <LeaseFieldLabel label="Maximum Upfront Payment" helpKey="maximumUpfrontPaymentAmount" field={`offerPrices.${index}.leasePricing.maximumUpfrontPaymentAmount`} />
                                  <CurrencyInput
                                      {...fieldAccessibility(`offerPrices.${index}.leasePricing.maximumUpfrontPaymentAmount`)}
                                      currencySymbol={currencySymbolForCode(row.leasePricing?.intendedSalePriceCurrency || value.currency)}
                                      value={row.leasePricing?.maximumUpfrontPaymentAmount ?? ""}
                                      onChange={e => patchOfferLeasePricing(index, { maximumUpfrontPaymentAmount: e.target.value || null })}
                                      className={cn(validationErrors[`offerPrices.${index}.leasePricing.maximumUpfrontPaymentAmount`] && 'border-destructive')}
                                  />
                                  <ValidationError field={`offerPrices.${index}.leasePricing.maximumUpfrontPaymentAmount`} message={validationErrors[`offerPrices.${index}.leasePricing.maximumUpfrontPaymentAmount`]} />
                              </div>
                          </LeaseSection>

                          { Number(row.leasePricing?.purchaseCostAmount ?? 0) > Number(row.leasePricing?.intendedSalePriceAmount ?? 0) && (row.leasePricing?.intendedSalePriceAmount) != null && (
                              <div className="sm:col-span-2 xl:col-span-3">
                                  <div className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
                                      <Info className="h-4 w-4" />
                                      <span className="font-semibold">Operator Warning:</span>
                                      <span>Intended sale price is below the internal purchase cost.</span>
                                  </div>
                              </div>
                          )}

                          <div className="sm:col-span-2 xl:col-span-3">
                            <p className={labelStyles}>Preview</p>
                            <div className="flex h-9 items-center rounded-md border border-transparent bg-muted/10 px-3 text-xs font-black text-foreground">
                              {formatLeasePreview(row)}
                            </div>
                          </div>
                        </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label htmlFor={pricebookFieldId(`offerPrices.${index}.amount`)} className={labelStyles}>Amount ({row.currency || value.currency})</label>
                              <input
                                {...fieldAccessibility(`offerPrices.${index}.amount`)}
                                value={row.amount || ""}
                                onChange={e => patchOfferRow(index, { amount: e.target.value })}
                                className={cn(inputStyles, validationErrors[`offerPrices.${index}.amount`] && "border-destructive")}
                                placeholder="0.00"
                              />
                              <ValidationError field={`offerPrices.${index}.amount`} message={validationErrors[`offerPrices.${index}.amount`]} />
                            </div>

                            <div>
                              <p className={labelStyles}>Preview</p>
                              <div className="flex h-9 items-center rounded-md border border-transparent bg-muted/10 px-3 text-xs font-black text-foreground">
                                {formatPricePreview(row, value.currency)}
                              </div>
                            </div>
                          </div>
                        )}
                        <details className="border-t border-border/40 pt-4">
                          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded-md px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                            <VerticalStack gap={1}>
                              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Offer-specific charges</p>
                              <DescriptionText className="text-[10px]">{(row.fees || []).length} {(row.fees || []).length === 1 ? "charge" : "charges"}</DescriptionText>
                            </VerticalStack>
                            <span className={cn(
                              "text-[10px] font-bold",
                              Object.keys(validationErrors).some(field => field.startsWith(`offerPrices.${index}.fees.`)) ? "text-destructive" : "text-primary",
                            )}>
                              {Object.keys(validationErrors).some(field => field.startsWith(`offerPrices.${index}.fees.`)) ? "Needs attention" : "View charges"}
                            </span>
                          </summary>
                          <div className="pt-3">
                            <button
                              type="button"
                              onClick={() => addOfferFeeRow(index)}
                              className="flex min-h-11 items-center gap-1.5 rounded-md border border-border/50 px-3 text-[10px] font-bold text-foreground transition-colors hover:bg-muted"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add charge
                            </button>

                          {(row.fees || []).length === 0 ? (
                            <p className="mt-3 rounded-md border border-dashed border-border/40 px-3 py-2 text-[10px] font-medium text-muted-foreground">
                              No offer-specific charges.
                            </p>
                          ) : (
                            <VerticalStack gap={3} className="mt-3">
                              {(row.fees || []).map((fee, feeIndex) => (
                                <div key={fee.rowId} className="grid grid-cols-1 gap-3 rounded-md border border-border/50 bg-muted/10 p-3 sm:grid-cols-3 xl:grid-cols-6">
                                  <div>
                                    <label htmlFor={pricebookFieldId(`offerPrices.${index}.fees.${feeIndex}.feeLabel`)} className={labelStyles}>Fee Label</label>
                                    <input
                                      {...fieldAccessibility(`offerPrices.${index}.fees.${feeIndex}.feeLabel`)}
                                      value={fee.feeLabel}
                                      onChange={e => patchOfferFeeRow(index, feeIndex, { feeLabel: e.target.value })}
                                      className={cn(inputStyles, validationErrors[`offerPrices.${index}.fees.${feeIndex}.feeLabel`] && "border-destructive")}
                                      placeholder="Installation"
                                    />
                                    <ValidationError field={`offerPrices.${index}.fees.${feeIndex}.feeLabel`} message={validationErrors[`offerPrices.${index}.fees.${feeIndex}.feeLabel`]} />
                                  </div>
                                  <div>
                                    <label htmlFor={pricebookFieldId(`offerPrices.${index}.fees.${feeIndex}.feeCode`)} className={labelStyles}>Code</label>
                                    <input
                                      {...fieldAccessibility(`offerPrices.${index}.fees.${feeIndex}.feeCode`)}
                                      value={fee.feeCode || ""}
                                      onChange={e => patchOfferFeeRow(index, feeIndex, { feeCode: e.target.value })}
                                      className={inputStyles}
                                      placeholder="install"
                                    />
                                  </div>
                                  <div>
                                    <label htmlFor={pricebookFieldId(`offerPrices.${index}.fees.${feeIndex}.chargeType`)} className={labelStyles}>Charge Type</label>
                                    <select
                                      {...fieldAccessibility(`offerPrices.${index}.fees.${feeIndex}.chargeType`)}
                                      value={fee.chargeType}
                                      onChange={e => patchOfferFeeRow(index, feeIndex, { chargeType: e.target.value as any })}
                                      className={inputStyles}
                                    >
                                      {chargeTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                  </div>
                                  {fee.chargeType === "percentage" ? (
                                    <div>
                                      <label htmlFor={pricebookFieldId(`offerPrices.${index}.fees.${feeIndex}.percentage`)} className={labelStyles}>Percentage</label>
                                      <input
                                        {...fieldAccessibility(`offerPrices.${index}.fees.${feeIndex}.percentage`)}
                                        value={fee.percentage || ""}
                                        onChange={e => patchOfferFeeRow(index, feeIndex, { percentage: e.target.value })}
                                        className={cn(inputStyles, validationErrors[`offerPrices.${index}.fees.${feeIndex}.percentage`] && "border-destructive")}
                                        placeholder="5.0"
                                      />
                                      <ValidationError field={`offerPrices.${index}.fees.${feeIndex}.percentage`} message={validationErrors[`offerPrices.${index}.fees.${feeIndex}.percentage`]} />
                                    </div>
                                  ) : fee.chargeType === "included" ? (
                                    <div className="flex items-end pb-2">
                                      <span className="rounded bg-muted px-2 py-1 text-[10px] font-bold text-foreground">Included</span>
                                    </div>
                                  ) : (
                                    <div>
                                      <label htmlFor={pricebookFieldId(`offerPrices.${index}.fees.${feeIndex}.amount`)} className={labelStyles}>Amount</label>
                                      <input
                                        {...fieldAccessibility(`offerPrices.${index}.fees.${feeIndex}.amount`)}
                                        value={fee.amount || ""}
                                        onChange={e => patchOfferFeeRow(index, feeIndex, { amount: e.target.value })}
                                        className={cn(inputStyles, validationErrors[`offerPrices.${index}.fees.${feeIndex}.amount`] && "border-destructive")}
                                        placeholder="0.00"
                                      />
                                      <ValidationError field={`offerPrices.${index}.fees.${feeIndex}.amount`} message={validationErrors[`offerPrices.${index}.fees.${feeIndex}.amount`]} />
                                    </div>
                                  )}
                                  <div>
                                    <label htmlFor={pricebookFieldId(`offerPrices.${index}.fees.${feeIndex}.currency`)} className={labelStyles}>Currency</label>
                                    <input
                                      {...fieldAccessibility(`offerPrices.${index}.fees.${feeIndex}.currency`)}
                                      value={fee.currency || value.currency || ""}
                                      onChange={e => patchOfferFeeRow(index, feeIndex, { currency: e.target.value.toUpperCase() })}
                                      className={inputStyles}
                                    />
                                  </div>
                                  {fee.chargeType === "recurring" && (
                                    <div>
                                      <label htmlFor={pricebookFieldId(`offerPrices.${index}.fees.${feeIndex}.billingPeriod`)} className={labelStyles}>Period</label>
                                      <select
                                        {...fieldAccessibility(`offerPrices.${index}.fees.${feeIndex}.billingPeriod`)}
                                        value={fee.billingPeriod || ""}
                                        onChange={e => patchOfferFeeRow(index, feeIndex, { billingPeriod: e.target.value })}
                                        className={cn(inputStyles, validationErrors[`offerPrices.${index}.fees.${feeIndex}.billingPeriod`] && "border-destructive")}
                                      >
                                        <option value="">Select...</option>
                                        {billingPeriodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                      </select>
                                      <ValidationError field={`offerPrices.${index}.fees.${feeIndex}.billingPeriod`} message={validationErrors[`offerPrices.${index}.fees.${feeIndex}.billingPeriod`]} />
                                    </div>
                                  )}
                                  <div className="flex items-end justify-end">
                                    <button
                                      type="button"
                                      onClick={() => removeOfferFeeRow(index, feeIndex)}
                                      className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                      aria-label="Remove offer-specific charge"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </VerticalStack>
                          )}
                          </div>
                        </details>
                      </VerticalStack>
                    </div>
  );

  const renderFeePriceEditor = (row: PricebookFeePriceRowViewModel, index: number) => {
    const amountField = `feePrices.${index}.${row.chargeType === "percentage" ? "percentage" : "amount"}`;
    return (
      <Surface className="group p-5 border-border/60">
        <VerticalStack gap={6}>
          <HorizontalStack className="items-start justify-between gap-4">
            <VerticalStack gap={1} className="min-w-0">
              <p className="truncate text-sm font-black text-foreground">{row.feeLabel}</p>
              <DescriptionText className="text-[11px]">{row.appliesToLabel || "Catalog fee"}</DescriptionText>
            </VerticalStack>
            <button
              type="button"
              onClick={() => removeFeeRow(index)}
              className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          </HorizontalStack>
          {validationErrors[`feePrices.${index}.feeId`] && (
            <div
              data-pricebook-field={`feePrices.${index}.feeId`}
              tabIndex={-1}
              aria-invalid="true"
              aria-describedby={fieldErrorId(`feePrices.${index}.feeId`)}
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
            >
              <ValidationError field={`feePrices.${index}.feeId`} message={validationErrors[`feePrices.${index}.feeId`]} />
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor={pricebookFieldId(`feePrices.${index}.chargeType`)} className={labelStyles}>Charge type</label>
              <select
                {...fieldAccessibility(`feePrices.${index}.chargeType`)}
                value={row.chargeType}
                onChange={event => patchFeeRow(index, { chargeType: event.target.value as PricebookChargeType })}
                className={inputStyles}
              >
                {chargeTypeOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            {row.chargeType === "percentage" ? (
              <div>
                <label htmlFor={pricebookFieldId(amountField)} className={labelStyles}>Percentage (%)</label>
                <input
                  {...fieldAccessibility(amountField)}
                  value={row.percentage || ""}
                  onChange={event => patchFeeRow(index, { percentage: event.target.value })}
                  className={cn(inputStyles, validationErrors[amountField] && "border-destructive")}
                  placeholder="5.0"
                />
                <ValidationError field={amountField} message={validationErrors[amountField]} />
              </div>
            ) : row.chargeType === "included" ? (
              <div className="flex items-end pb-2 text-xs font-semibold text-muted-foreground">No customer charge</div>
            ) : (
              <div>
                <label htmlFor={pricebookFieldId(amountField)} className={labelStyles}>Amount ({row.currency || value.currency})</label>
                <input
                  {...fieldAccessibility(amountField)}
                  value={row.amount || ""}
                  onChange={event => patchFeeRow(index, { amount: event.target.value })}
                  className={cn(inputStyles, validationErrors[amountField] && "border-destructive")}
                  placeholder="0.00"
                />
                <ValidationError field={amountField} message={validationErrors[amountField]} />
              </div>
            )}
          </div>
          <DescriptionText className="text-[11px]">
            Preview: <span className="font-semibold text-foreground">{formatPricePreview(row, value.currency)}</span>
          </DescriptionText>
        </VerticalStack>
      </Surface>
    );
  };

  return (
    <div
      ref={workspaceRef}
      className={cn("flex h-full min-h-0 w-full flex-col overflow-hidden bg-background text-foreground", !isOpen && "hidden", className)}
    >
      <header className="flex min-h-14 shrink-0 items-center gap-3 border-b border-border/60 px-3 sm:px-5">
        <button
          type="button"
          onClick={onClose}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Back to pricebooks"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="truncate text-base font-bold tracking-tight">{value.name || "Untitled Pricebook"}</h1>
            <span className="hidden rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold capitalize text-muted-foreground sm:inline-flex">
              {value.status || "draft"}
            </span>
            {dirtyCount > 0 && (
              <span className="hidden text-[10px] font-semibold text-primary sm:inline">
                {dirtyCount} {dirtyCount === 1 ? "change" : "changes"}
              </span>
            )}
          </div>
          <p className="truncate text-[11px] text-muted-foreground">{viewModel.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={isSaving || isLoading}
          className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-md bg-primary px-3 text-xs font-bold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 sm:px-4"
        >
          {isSaving ? "Saving…" : saveActionLabel}
        </button>
      </header>

      <nav className="flex h-11 shrink-0 items-end gap-4 overflow-x-auto border-b border-border/60 px-3 sm:gap-5 sm:px-6" aria-label="Pricebook editor">
        {([
          { id: "settings", label: "Settings", icon: Settings },
          { id: "prices", label: "Prices", icon: Tag },
          { id: "quote_policy", label: "Quote policy", icon: SlidersHorizontal },
          { id: "review", label: "Review", icon: ClipboardCheck },
        ] as const).map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onDestinationChange(item.id)}
              aria-current={activeDestination === item.id ? "page" : undefined}
              className={cn(
                "flex h-11 shrink-0 items-center gap-1.5 border-b-2 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                activeDestination === item.id
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {item.label}
              {item.id === "review" && validationIssues.length > 0 && (
                <span className="rounded-full bg-destructive/10 px-1.5 text-[9px] text-destructive">{validationIssues.length}</span>
              )}
            </button>
          );
        })}
      </nav>

      <main className="min-h-0 flex-1 overflow-hidden">
      {isLoading ? (
        <div className="flex h-full items-center justify-center text-sm font-semibold text-muted-foreground" role="status">
          Loading pricebook…
        </div>
      ) : (
        <>
      {activeDestination === "settings" && (
        <VerticalStack gap={8} className="h-full overflow-y-auto px-4 py-5 sm:px-6">
          <section>
            <SectionHeading title="Settings" subtitle="Define where this sparse pricing policy applies and how amounts are interpreted." />
            
            <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-3">
                <div className="border-y border-border/60 py-6">
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                    <Field label="Pricebook Name" htmlFor={pricebookFieldId("name")}>
                      <input 
                        {...fieldAccessibility("name")}
                        value={value.name}
                        onChange={e => onChange({ name: e.target.value })}
                        className={inputStyles}
                        placeholder="e.g. Winter Sale 2026"
                      />
                      <ValidationError field="name" message={validationErrors.name} />
                    </Field>
                    <Field label="Status" htmlFor={pricebookFieldId("status")}>
                      <select 
                        {...fieldAccessibility("status")}
                        value={value.status}
                        onChange={e => onChange({ status: e.target.value })}
                        className={inputStyles}
                      >
                        {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Currency" htmlFor={pricebookFieldId("currency")}>
                      <select 
                        {...fieldAccessibility("currency")}
                        value={value.currency || ""}
                        onChange={e => onChange({ currency: e.target.value })}
                        className={inputStyles}
                      >
                        {currencyOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Field>
                    <Field label="Pricebook type" htmlFor={pricebookFieldId("pricingStrategy")}>
                      <select 
                        {...fieldAccessibility("pricingStrategy")}
                        value={value.pricingStrategy}
                        onChange={e => onChange({ pricingStrategy: e.target.value as any })}
                        className={inputStyles}
                      >
                        {pricingStrategyOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Seller scope" htmlFor={pricebookFieldId("sellingTenantId")}>
                        <select 
                          {...fieldAccessibility("sellingTenantId")}
                          value={value.sellingTenantId || ""}
                          onChange={e => onChange({ sellingTenantId: e.target.value })}
                          className={inputStyles}
                        >
                          <option value="">Catalog owner default (Universal)</option>
                          {sellingTenantOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <DescriptionText className="mt-2 text-[11px]">
                          Scoped pricebooks take precedence over universal ones when resolved for a specific reseller.
                        </DescriptionText>
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Catalog" htmlFor={pricebookFieldId("catalogId")}>
                        <select
                          {...fieldAccessibility("catalogId")}
                          value={value.catalogId || ""}
                          onChange={e => onChange({ catalogId: e.target.value || null })}
                          className={inputStyles}
                        >
                          <option value="">All catalogs</option>
                          {catalogOptions.map(o => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
                        </select>
                        <ValidationError field="catalogId" message={validationErrors.catalogId} />
                        <DescriptionText className="mt-2 text-[11px]">
                          Limiting to one catalog makes only its products and fees available. Changing an existing pricebook&apos;s catalog clears its current pricing rows so it can be repriced safely in the new scope.
                        </DescriptionText>
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field
                        label="Allowed Term Months"
                        htmlFor={pricebookFieldId("leaseTerms.allowedTermMonthsText")}
                        description={leaseFieldHelpText.allowedTermMonths}
                      >
                        <input
                          {...fieldAccessibility("leaseTerms.allowedTermMonthsText")}
                          value={value.leaseTerms.allowedTermMonthsText}
                          onChange={e => onChange({ leaseTerms: { ...value.leaseTerms, allowedTermMonthsText: e.target.value } })}
                          className={inputStyles}
                          placeholder="24, 36, 48"
                        />
                        <ValidationError field="leaseTerms.allowedTermMonthsText" message={validationErrors["leaseTerms.allowedTermMonthsText"]} />
                      </Field>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            <details className="mt-6 rounded-lg border border-border/60 bg-muted/5">
              <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-4 text-xs font-black text-foreground">
                <Database className="h-4 w-4 text-muted-foreground" />
                Advanced
              </summary>
              <div className="border-t border-border/50 p-5">
                <DescriptionText className="mb-4 font-mono text-[11px]">
                  Internal ID: {viewModel.pricebookId || "assigned on first save"}
                </DescriptionText>
                <VerticalStack gap={3}>
                  {value.metadata.map((entry, index) => (
                    <HorizontalStack key={entry.id} gap={3} className="items-center">
                      <input
                        aria-label={`Metadata key ${index + 1}`}
                        value={entry.key}
                        onChange={event => onChange({ metadata: value.metadata.map((item, itemIndex) => itemIndex === index ? { ...item, key: event.target.value } : item) })}
                        placeholder="Key"
                        className={cn(inputStyles, "font-mono")}
                      />
                      <input
                        aria-label={`Metadata value ${index + 1}`}
                        value={entry.value}
                        onChange={event => onChange({ metadata: value.metadata.map((item, itemIndex) => itemIndex === index ? { ...item, value: event.target.value } : item) })}
                        placeholder="Value"
                        className={cn(inputStyles, "font-mono")}
                      />
                      <button
                        type="button"
                        onClick={() => onChange({ metadata: value.metadata.filter((_, itemIndex) => itemIndex !== index) })}
                        className="p-2 text-muted-foreground transition-colors hover:text-destructive"
                        aria-label={`Remove metadata property ${entry.key || index + 1}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </HorizontalStack>
                  ))}
                  <button
                    type="button"
                    onClick={() => onChange({ metadata: [...value.metadata, { id: `m-${Date.now()}`, key: "", value: "", valueType: "string" }] })}
                    className="flex items-center gap-2 self-start rounded-md border border-border/50 px-3 py-2 text-xs font-bold text-foreground transition-colors hover:bg-muted"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add property
                  </button>
                </VerticalStack>
              </div>
            </details>
          </section>
        </VerticalStack>
      )}

      {activeDestination === "prices" && (
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex min-h-12 shrink-0 items-center gap-3 border-b border-border/60 px-4 sm:px-6">
            <div className="flex items-center gap-1" role="tablist" aria-label="Price scope">
              {([
                ["product_prices", "Product prices", directoryCounts.priced],
                ["catalog_fees", "Catalog fees", value.feePrices.length],
              ] as const).map(([scope, label, count]) => (
                <button
                  key={scope}
                  type="button"
                  role="tab"
                  aria-selected={activePriceScope === scope}
                  onClick={() => onPriceScopeChange(scope)}
                  className={cn(
                    "min-h-11 rounded-md px-3 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    activePriceScope === scope ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label} <span className="ml-1 text-[10px] text-muted-foreground">{count}</span>
                </button>
              ))}
            </div>
          </div>

          {activePriceScope === "product_prices" ? (
            <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(400px,460px)_minmax(0,1fr)]">
              <section className={cn(
                "relative flex min-h-0 flex-col border-r border-border/60 bg-background",
                selectedPriceRowId ? "hidden lg:flex" : "flex",
              )} aria-label="Product price directory">
                <div className="shrink-0 border-b border-border/50 p-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={directoryQuery.search}
                      onChange={event => onDirectoryQueryChange({ search: event.target.value })}
                      placeholder="Search products, variants, or offers"
                      aria-label="Search product prices"
                      className="h-9 w-full rounded-md border border-border/60 bg-background pl-8 pr-3 text-xs outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/50 focus:ring-1 focus:ring-ring"
                    />
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1 sm:flex sm:overflow-x-auto sm:pb-1" aria-label="Price views">
                    {([
                      ["all_current", "All current", directoryCounts.allCurrent],
                      ["priced", "Priced", directoryCounts.priced],
                      ["not_in_pricebook", "Not in this pricebook", directoryCounts.notInPricebook],
                      ["needs_attention", "Needs attention", directoryCounts.needsAttention],
                      ["historical", "Historical pricing", directoryCounts.historical],
                    ] as const).map(([view, label, count]) => (
                      <button
                        key={view}
                        type="button"
                        onClick={() => onDirectoryQueryChange({ view })}
                        aria-pressed={directoryQuery.view === view}
                        className={cn(
                          "min-h-11 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-[10px] font-bold transition-colors",
                          directoryQuery.view === view
                            ? "border-primary/40 bg-primary/10 text-primary"
                            : "border-border/50 text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {label} <span className="ml-0.5 opacity-75">{count}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileFiltersOpen(true)}
                    aria-expanded={mobileFiltersOpen}
                    aria-controls="pricebook-mobile-filters"
                    className="mt-2 inline-flex min-h-11 w-full items-center justify-center gap-1.5 rounded-md border border-border/60 text-xs font-bold text-foreground lg:hidden"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    Filters and sort{directoryQuery.activeFilterCount > 0 ? ` (${directoryQuery.activeFilterCount})` : ""}
                  </button>
                  {mobileFiltersOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden" role="presentation">
                      <button
                        type="button"
                        aria-label="Close filters"
                        onClick={() => setMobileFiltersOpen(false)}
                        className="absolute inset-0 bg-foreground/25"
                      />
                      <section
                        id="pricebook-mobile-filters"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pricebook-mobile-filters-title"
                        className="absolute inset-x-0 bottom-0 rounded-t-xl border-t border-border bg-background p-4 pb-20 shadow-lg"
                      >
                        <div className="mb-4 flex min-h-11 items-center justify-between gap-3">
                          <div>
                            <h3 id="pricebook-mobile-filters-title" className="text-sm font-bold">Filters and sort</h3>
                            <p className="mt-0.5 text-[11px] text-muted-foreground">Narrow the product price directory.</p>
                          </div>
                          <button type="button" autoFocus onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters" className="flex min-h-11 min-w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid gap-4">
                          <Field label="Product" htmlFor="pricebook-mobile-product-filter">
                            <select
                              id="pricebook-mobile-product-filter"
                              value={directoryQuery.productIds[0] || ""}
                              onChange={event => onDirectoryQueryChange({ productIds: event.target.value ? [event.target.value] : [] })}
                              className={inputStyles}
                            >
                              <option value="">All products</option>
                              {productFacetOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                          </Field>
                          <Field label="Offer type" htmlFor="pricebook-mobile-offer-type-filter">
                            <select
                              id="pricebook-mobile-offer-type-filter"
                              value={directoryQuery.offerTypes[0] || ""}
                              onChange={event => onDirectoryQueryChange({ offerTypes: event.target.value ? [event.target.value] : [] })}
                              className={inputStyles}
                            >
                              <option value="">All offer types</option>
                              {offerTypeFacetOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                          </Field>
                          <Field label="Sort" htmlFor="pricebook-mobile-sort">
                            <select
                              id="pricebook-mobile-sort"
                              value={directoryQuery.sort}
                              onChange={event => onDirectoryQueryChange({ sort: event.target.value as typeof directoryQuery.sort })}
                              className={inputStyles}
                            >
                              <option value="product_asc">Product A–Z</option>
                              <option value="product_desc">Product Z–A</option>
                              <option value="state">State</option>
                              <option value="price_asc">Price low–high</option>
                              <option value="price_desc">Price high–low</option>
                            </select>
                          </Field>
                        </div>
                        <button type="button" onClick={() => setMobileFiltersOpen(false)} className="mt-5 min-h-11 w-full rounded-md bg-primary px-4 text-xs font-bold text-primary-foreground">Show results</button>
                      </section>
                    </div>
                  )}
                  <div className="mt-2 hidden grid-cols-3 gap-1.5 lg:grid">
                    <label className="sr-only" htmlFor="pricebook-product-filter">Product</label>
                    <select
                      id="pricebook-product-filter"
                      value={directoryQuery.productIds[0] || ""}
                      onChange={event => onDirectoryQueryChange({ productIds: event.target.value ? [event.target.value] : [] })}
                      className="h-8 min-w-0 rounded-md border border-border/50 bg-background px-2 text-[10px] font-semibold"
                    >
                      <option value="">All products</option>
                      {productFacetOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <label className="sr-only" htmlFor="pricebook-offer-type-filter">Offer type</label>
                    <select
                      id="pricebook-offer-type-filter"
                      value={directoryQuery.offerTypes[0] || ""}
                      onChange={event => onDirectoryQueryChange({ offerTypes: event.target.value ? [event.target.value] : [] })}
                      className="h-8 min-w-0 rounded-md border border-border/50 bg-background px-2 text-[10px] font-semibold"
                    >
                      <option value="">All offer types</option>
                      {offerTypeFacetOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <label className="sr-only" htmlFor="pricebook-sort">Sort</label>
                    <select
                      id="pricebook-sort"
                      value={directoryQuery.sort}
                      onChange={event => onDirectoryQueryChange({ sort: event.target.value as typeof directoryQuery.sort })}
                      className="h-8 min-w-0 rounded-md border border-border/50 bg-background px-2 text-[10px] font-semibold"
                    >
                      <option value="product_asc">Product A–Z</option>
                      <option value="product_desc">Product Z–A</option>
                      <option value="state">State</option>
                      <option value="price_asc">Price low–high</option>
                      <option value="price_desc">Price high–low</option>
                    </select>
                  </div>
                  {directoryQuery.activeFilterCount > 0 && (
                    <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><SlidersHorizontal className="h-3 w-3" /> {directoryQuery.activeFilterCount} active</span>
                      <button
                        type="button"
                        onClick={() => onDirectoryQueryChange({ search: "", productIds: [], offerTypes: [], sort: "product_asc" })}
                        className="inline-flex items-center gap-1 font-bold text-foreground hover:text-primary"
                      >
                        <RotateCcw className="h-3 w-3" /> Reset
                      </button>
                    </div>
                  )}
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto" data-pricebook-directory>
                  {productGroups.length === 0 ? (
                    <div className="px-6 py-12 text-center">
                      <p className="text-sm font-bold">No matching products</p>
                      <p className="mt-1 text-xs text-muted-foreground">Adjust the search or filters. Unpriced offers remain optional.</p>
                    </div>
                  ) : productGroups.map(group => (
                    <div key={group.productId} className="border-b border-border/50">
                      <button
                        type="button"
                        onClick={() => onToggleProductExpanded(group.productId)}
                        aria-expanded={group.isExpanded}
                        className="sticky top-0 z-[1] flex min-h-[52px] w-full items-center gap-2 bg-background px-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      >
                        {group.isExpanded ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-bold text-foreground">{group.productLabel}</span>
                          <span className="mt-0.5 block text-[10px] text-muted-foreground">
                            {group.classification === "historical"
                              ? `${group.counts.historical} historical`
                              : `${group.counts.priced} priced · ${group.counts.notInPricebook} not in pricebook${group.counts.needsAttention ? ` · ${group.counts.needsAttention} need attention` : ""}`}
                          </span>
                        </span>
                        <span className="text-[10px] font-semibold text-muted-foreground">{group.counts.current || group.counts.historical}</span>
                      </button>
                      {group.isExpanded && (
                        <div>
                          {group.rows.map(row => (
                              <button
                                key={`${row.offerId}:${row.rowId}`}
                                type="button"
                                onClick={() => onOpenOfferPrice(row.offerId)}
                                aria-label={`${row.productLabel}. ${row.variantLabel}. ${row.offerLabel}. ${row.currentPriceLabel}. ${row.pricingPresence === "priced" ? "Open price" : "Not in pricebook; start a price draft"}`}
                                aria-current={row.isSelected ? "true" : undefined}
                                className={cn(
                                  "flex min-h-[58px] w-full items-start gap-2 border-t border-border/40 py-2.5 pl-9 pr-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                                  row.isSelected ? "bg-primary/10" : "hover:bg-muted/35",
                                )}
                              >
                                <span className="min-w-0 flex-1">
                                  <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                                    <span className="text-xs font-bold text-foreground">{row.variantLabel}</span>
                                    <span className="text-[10px] text-muted-foreground">· {row.offerLabel}</span>
                                    <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground">{row.offerTypeLabel}</span>
                                  </span>
                                  {row.offerSpecificChargeCount > 0 && (
                                    <span className="mt-1 block text-[9px] text-muted-foreground">{row.offerSpecificChargeCount} offer-specific {row.offerSpecificChargeCount === 1 ? "charge" : "charges"}</span>
                                  )}
                                </span>
                                <span className="flex shrink-0 flex-col items-end gap-1">
                                  <span className="max-w-32 text-right text-[10px] font-semibold text-foreground">{row.currentPriceLabel}</span>
                                  {row.pricingPresence === "priced"
                                    ? <LifecycleBadge lifecycle={row.lifecycle} />
                                    : <span className="text-[10px] font-medium text-muted-foreground">Not in pricebook</span>}
                                </span>
                              </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <section className={cn("min-h-0 overflow-y-auto bg-background", !selectedPriceRowId ? "hidden lg:block" : "block")} aria-label="Price inspector">
                {selectedPriceRowId && (
                  <button type="button" onClick={() => onSelectPriceRow(null)} className="mx-4 mt-4 inline-flex min-h-11 items-center gap-1.5 text-xs font-bold text-muted-foreground lg:hidden">
                    <ArrowLeft className="h-3.5 w-3.5" /> Product prices
                  </button>
                )}
                {selectedInspector && selectedOfferEntry >= 0 ? (
                  <>
                    <div className="sticky top-0 z-[2] border-b border-border/60 bg-background/95 px-4 py-4 backdrop-blur sm:px-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{selectedInspector.productLabel}</p>
                          <h2 className="mt-1 text-base font-bold text-foreground">{selectedInspector.variantLabel}</h2>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>{selectedInspector.offerLabel}</span><span>·</span><span>{selectedInspector.offerTypeLabel}</span>
                            <LifecycleBadge lifecycle={selectedInspector.lifecycle} />
                          </div>
                          <p className="mt-2 text-xs font-semibold text-foreground">{selectedInspector.currentPriceLabel}</p>
                        </div>
                        {selectedInspector.isDraft && selectedInspector.canDiscardDraft ? (
                          <button type="button" onClick={() => onDiscardPriceDraft(selectedInspector.rowId)} className="inline-flex min-h-11 items-center gap-1.5 rounded-md px-3 text-xs font-bold text-destructive hover:bg-destructive/10">
                            <Trash2 className="h-3.5 w-3.5" /> Discard draft
                          </button>
                        ) : selectedInspector.canUndoRemoval ? (
                          <button type="button" onClick={() => onUndoPriceRowRemoval(selectedInspector.rowId)} className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-bold text-foreground hover:bg-muted">
                            <Undo2 className="h-3.5 w-3.5" /> Undo removal
                          </button>
                        ) : selectedInspector.canRemove ? (
                          <button type="button" onClick={() => onMarkPriceRowForRemoval(selectedInspector.rowId)} className="inline-flex min-h-11 items-center gap-1.5 rounded-md px-3 text-xs font-bold text-destructive hover:bg-destructive/10">
                            <Trash2 className="h-3.5 w-3.5" /> Mark for removal
                          </button>
                        ) : null}
                      </div>
                      {selectedInspector.classification === "historical" && (
                        <p className="mt-3 rounded-md bg-muted px-3 py-2 text-[11px] text-muted-foreground">
                          This price references a retired catalog offer. It is preserved for history and cannot be edited.
                        </p>
                      )}
                    </div>
                    <fieldset disabled={!selectedInspector.canEdit} className="min-w-0 disabled:opacity-70">
                      {renderOfferPriceEditor(value.offerPrices[selectedOfferEntry], selectedOfferEntry)}
                    </fieldset>
                  </>
                ) : (
                  <div className="flex h-full min-h-64 flex-col items-center justify-center px-6 text-center">
                    <Tag className="h-7 w-7 text-muted-foreground/40" />
                    <p className="mt-3 text-sm font-bold">Select an offer</p>
                    <p className="mt-1 max-w-sm text-xs text-muted-foreground">Priced offers open for editing. Unpriced offers start a draft.</p>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)]">
              <section className={cn("min-h-0 overflow-y-auto border-r border-border/60", selectedFeeEntry >= 0 ? "hidden lg:block" : "block")} aria-label="Catalog fees">
                <div className="sticky top-0 z-[1] flex items-center justify-between border-b border-border/50 bg-background px-4 py-3">
                  <div><p className="text-xs font-bold">Catalog fees</p><p className="text-[10px] text-muted-foreground">Pricebook-wide charges</p></div>
                  {unpricedFeeTargets.length > 0 && (
                    <select
                      aria-label="Add catalog fee"
                      value=""
                      onChange={event => event.target.value && addFeeRow(event.target.value)}
                      className="h-8 rounded-md border border-border/60 bg-background px-2 text-[10px] font-bold"
                    >
                      <option value="">Add fee…</option>
                      {unpricedFeeTargets.map(target => <option key={target.value} value={target.value}>{target.label}</option>)}
                    </select>
                  )}
                </div>
                {value.feePrices.length === 0 ? (
                  <div className="px-6 py-12 text-center"><DollarSign className="mx-auto h-6 w-6 text-muted-foreground/40" /><p className="mt-3 text-sm font-bold">No catalog fees</p><p className="mt-1 text-xs text-muted-foreground">Add only the pricebook-wide charges this policy needs.</p></div>
                ) : value.feePrices.map(row => (
                  <button key={row.rowId} type="button" onClick={() => onSelectPriceRow(row.rowId)} className={cn("flex min-h-[58px] w-full items-center justify-between gap-3 border-b border-border/40 px-4 text-left hover:bg-muted/35", selectedPriceRowId === row.rowId && "bg-primary/10")}>
                    <span className="min-w-0"><span className="block text-xs font-bold">{row.feeLabel}</span><span className="mt-0.5 block text-[10px] text-muted-foreground">{row.appliesToLabel || "Catalog fee"}</span></span>
                    <span className="shrink-0 text-[10px] font-semibold">{formatPricePreview(row, value.currency)}</span>
                  </button>
                ))}
              </section>
              <section className={cn("min-h-0 overflow-y-auto p-4 sm:p-6", selectedFeeEntry < 0 ? "hidden lg:block" : "block")} aria-label="Catalog fee inspector">
                {selectedFeeEntry >= 0 ? (
                  <><button type="button" onClick={() => onSelectPriceRow(null)} className="mb-4 inline-flex min-h-11 items-center gap-1.5 text-xs font-bold text-muted-foreground lg:hidden"><ArrowLeft className="h-3.5 w-3.5" /> Catalog fees</button>{renderFeePriceEditor(value.feePrices[selectedFeeEntry], selectedFeeEntry)}</>
                ) : (
                  <div className="flex h-full items-center justify-center text-center"><div><DollarSign className="mx-auto h-7 w-7 text-muted-foreground/40" /><p className="mt-3 text-sm font-bold">Select a catalog fee</p><p className="mt-1 text-xs text-muted-foreground">Choose a fee to inspect or edit it.</p></div></div>
                )}
              </section>
            </div>
          )}
        </div>
      )}

      {activeDestination === "review" && (
        <div className="h-full overflow-y-auto px-4 py-5 sm:px-6">
          <div className="mx-auto max-w-5xl">
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border/60 pb-4">
              <div>
                <h2 className="text-base font-bold">Review changes</h2>
                <p className="mt-1 text-xs text-muted-foreground">Confirm the exact settings, prices, removals, and fees that will be saved.</p>
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                <span><strong className="text-foreground">{dirtyCount}</strong> pending</span>
                <span className={validationIssues.length ? "text-destructive" : "text-primary"}>
                  <strong>{validationIssues.length}</strong> blocking
                </span>
              </div>
            </div>

            {reviewChangeGroups.every(group => group.items.length === 0) ? (
              <div className="py-16 text-center">
                <CheckCircle2 className="mx-auto h-7 w-7 text-primary" />
                <p className="mt-3 text-sm font-bold">No pending changes</p>
                <p className="mt-1 text-xs text-muted-foreground">Unpriced catalog offers are optional and do not block this pricebook.</p>
              </div>
            ) : (
              <div className="divide-y divide-border/60">
                {reviewChangeGroups.filter(group => group.items.length > 0).map(group => (
                  <section key={group.id} className="py-5" aria-labelledby={`review-${group.id}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 id={`review-${group.id}`} className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{group.label}</h3>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">{group.count}</span>
                    </div>
                    <div className="divide-y divide-border/40 border-y border-border/40">
                      {group.items.map(item => {
                        const blocking = item.kind === "blocking_issue";
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => navigateToChange(item)}
                            className={cn(
                              "flex min-h-[58px] w-full items-start gap-3 px-2 py-3 text-left transition-colors hover:bg-muted/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                              blocking && "bg-destructive/5",
                            )}
                          >
                            {blocking ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                            <span className="min-w-0 flex-1">
                              <span className="block text-xs font-bold text-foreground">{item.label}</span>
                              {item.contextLabel && <span className="mt-0.5 block text-[10px] text-muted-foreground">{item.contextLabel}</span>}
                              {(item.beforeValue != null || item.afterValue != null) && (
                                <span className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px]">
                                  <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">{item.beforeValue || "Not set"}</span>
                                  <span className="text-muted-foreground">→</span>
                                  <span className="rounded bg-primary/10 px-1.5 py-0.5 font-semibold text-primary">{item.afterValue || "Removed"}</span>
                                </span>
                              )}
                            </span>
                            <span className="shrink-0 text-[10px] font-bold text-primary">Open</span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {activeDestination === "quote_policy" && (
        <VerticalStack gap={8} className="h-full overflow-y-auto px-4 py-5 sm:px-6">
          <section>
            <SectionHeading title="Quote policy destination" subtitle="Define the policies for calculating quotes and applying taxes." />

            <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-3">
                <div className="border-y border-border/60 py-6">
                  <VerticalStack gap={6}>
                    <fieldset>
                      <legend className={labelStyles}>Construction pricing mode</legend>
                      <DescriptionText className="mb-2 text-[11px]">How prices are generated for construction quotes.</DescriptionText>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        {([
                          { value: "automatic", label: "Automatic", description: "Use route pricing when usable route evidence exists; otherwise request an external estimate when construction is needed." },
                          { value: "not_required", label: "No construction charge", description: "Do not add a construction charge to newly frozen quotes." },
                          { value: "commerce_rate", label: "Commerce route rate", description: "Require Commerce route distance, build type, rate, and minimum build cost." },
                          { value: "external_cost", label: "External estimator", description: "Wait for an authorized service to submit distance and build cost." },
                        ] as const).map(option => (
                          <div key={option.value} className="flex items-center">
                            <input
                              type="radio"
                              id={pricebookFieldId(`quotePolicy.constructionPricingMode-${option.value}`)}
                              name="constructionPricingMode"
                              value={option.value}
                              checked={value.quotePolicy.constructionPricingMode === option.value}
                              onChange={() => onChange({ quotePolicy: { ...value.quotePolicy, constructionPricingMode: option.value } })}
                              className="h-4 w-4 text-primary focus:ring-primary border-border/60"
                            />
                            <label htmlFor={pricebookFieldId(`quotePolicy.constructionPricingMode-${option.value}`)} className="ml-2 text-sm font-medium text-foreground">
                              {option.label}
                            </label>
                            <FieldHelpTooltip label={option.label} text={option.description} />
                          </div>
                        ))}
                      </div>
                    </fieldset>

                    <Field label="Customer Markup (%)" htmlFor={pricebookFieldId("quotePolicy.constructionCustomerMarkupPercent")}>
                      <PercentageInput
                        {...fieldAccessibility("quotePolicy.constructionCustomerMarkupPercent")}
                        value={value.quotePolicy.constructionCustomerMarkupPercent}
                        onChange={e => onChange({ quotePolicy: { ...value.quotePolicy, constructionCustomerMarkupPercent: e.target.value } })}
                        placeholder="0.00"
                      />
                      <ValidationError field="quotePolicy.constructionCustomerMarkupPercent" message={validationErrors["quotePolicy.constructionCustomerMarkupPercent"]} />
                      <DescriptionText className="mt-2 text-[11px]">{viewModel.quotePolicy.markupPreviewLabel}</DescriptionText>
                    </Field>

                    {(value.quotePolicy.constructionPricingMode === "automatic" || value.quotePolicy.constructionPricingMode === "commerce_rate") && viewModel.quotePolicy.readinessLinks.length > 0 && (
                      <div>
                        <p className={labelStyles}>Readiness</p>
                        <VerticalStack gap={2} className="mt-2">
                          {viewModel.quotePolicy.readinessLinks.map(link => (
                            <div key={link.id} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 text-xs">
                              <div className="flex items-center gap-2">
                                {link.isReady ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <AlertCircle className="h-4 w-4 text-destructive" />}
                                <VerticalStack gap={0}>
                                  <p className="font-bold">{link.label}</p>
                                  <DescriptionText className="text-[10px]">{link.detail}</DescriptionText>
                                </VerticalStack>
                              </div>
                              <button
                                type="button"
                                onClick={() => viewModel.onQuotePolicyNavigate(link.href)}
                                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold text-primary hover:bg-primary/10"
                              >
                                View
                              </button>
                            </div>
                          ))}
                        </VerticalStack>
                      </div>
                    )}

                    {(value.quotePolicy.constructionPricingMode === "automatic" || value.quotePolicy.constructionPricingMode === "external_cost") && (
                      <fieldset>
                        <legend className={labelStyles}>Estimator Authorization</legend>
                        <DescriptionText className="mb-2 text-[11px]">Control which external estimators can be used.</DescriptionText>
                        <div className="flex gap-4">
                          {([
                            { value: "any_authorized", label: "Any Authorized", description: "Any authorized external estimator can be used." },
                            { value: "selected", label: "Selected Only", description: "Only selected external estimators can be used." },
                          ] as const).map(option => (
                            <div key={option.value} className="flex items-center">
                              <input
                                type="radio"
                                id={pricebookFieldId(`quotePolicy.constructionEstimatorAuthorization-${option.value}`)}
                                name="constructionEstimatorAuthorization"
                                value={option.value}
                                checked={value.quotePolicy.constructionEstimatorAuthorization === option.value}
                                onChange={() => onChange({ quotePolicy: { ...value.quotePolicy, constructionEstimatorAuthorization: option.value } })}
                                disabled={viewModel.quotePolicy.isReadOnly}
                                className="h-4 w-4 text-primary focus:ring-primary border-border/60"
                              />
                              <label htmlFor={pricebookFieldId(`quotePolicy.constructionEstimatorAuthorization-${option.value}`)} className="ml-2 text-sm font-medium text-foreground">
                                {option.label}
                              </label>
                              <FieldHelpTooltip label={option.label} text={option.description} />
                            </div>
                          ))}
                        </div>
                        {viewModel.quotePolicy.estimatorOptions.status === "unavailable" && (
                          <p className="mt-2 text-xs text-muted-foreground">{viewModel.quotePolicy.estimatorOptions.message}</p>
                        )}
                        {viewModel.quotePolicy.estimatorOptions.status === "error" && (
                          <div className="mt-2 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                            <AlertCircle className="h-4 w-4" />
                            <span className="font-semibold">Error:</span>
                            <span>{viewModel.quotePolicy.estimatorOptions.message || "Could not load estimator options."}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => viewModel.onQuotePolicyNavigate("/api-gateway/access")}
                          className="mt-3 inline-flex min-h-11 items-center rounded-md border border-border/60 px-3 text-xs font-bold text-foreground hover:bg-muted"
                        >
                          Manage estimator access
                        </button>
                        {value.quotePolicy.constructionEstimatorAuthorization === "selected" && viewModel.quotePolicy.estimatorOptions.options.length > 0 && (
                          <VerticalStack gap={2} className="mt-4">
                            {viewModel.quotePolicy.estimatorOptions.options.map(option => (
                              <div key={option.serviceAccountId} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 text-xs">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    id={pricebookFieldId(`quotePolicy.estimator-${option.serviceAccountId}`)}
                                    checked={value.quotePolicy.constructionEstimatorServiceAccountIds.includes(option.serviceAccountId)}
                                    onChange={e => {
                                      const currentIds = value.quotePolicy.constructionEstimatorServiceAccountIds;
                                      const nextIds = e.target.checked
                                        ? [...currentIds, option.serviceAccountId]
                                        : currentIds.filter(id => id !== option.serviceAccountId);
                                      onChange({ quotePolicy: { ...value.quotePolicy, constructionEstimatorServiceAccountIds: nextIds } });
                                    }}
                                    disabled={viewModel.quotePolicy.isReadOnly || !option.isSelectable}
                                    className="h-4 w-4 text-primary focus:ring-primary border-border/60"
                                  />
                                  <label htmlFor={pricebookFieldId(`quotePolicy.estimator-${option.serviceAccountId}`)} className="text-sm font-medium text-foreground">
                                    {option.label}
                                  </label>
                                  {option.credentialLabels.map((label, i) => <span key={i} className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground">{label}</span>)}
                                  {option.status !== "active" && <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground">{option.statusLabel}</span>}
                                </div>
                                {!option.isSelectable && <span className="text-[10px] text-muted-foreground">Not selectable</span>}
                              </div>
                            ))}
                            {viewModel.quotePolicy.estimatorOptions.unresolvedSelectedServiceAccountIds.length > 0 && (
                              <div className="mt-2 flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
                                <AlertCircle className="h-4 w-4" />
                                <span className="font-semibold">Warning:</span>
                                <span>Some selected estimators are unresolved or unavailable.</span>
                              </div>
                            )}
                          </VerticalStack>
                        )}
                      </fieldset>
                    )}

                    <HorizontalStack gap={4} className="items-center">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={pricebookFieldId("quotePolicy.constructionTaxable")}
                          checked={value.quotePolicy.constructionTaxable}
                          onChange={e => onChange({ quotePolicy: { ...value.quotePolicy, constructionTaxable: e.target.checked } })}
                          className="h-4 w-4 text-primary focus:ring-primary border-border/60"
                        />
                        <label htmlFor={pricebookFieldId("quotePolicy.constructionTaxable")} className="ml-2 text-sm font-medium text-foreground">
                          Construction taxable
                        </label>
                      </div>
                      <Field label="Tax category" htmlFor={pricebookFieldId("quotePolicy.constructionTaxCategory")} className="flex-1">
                        <input
                          {...fieldAccessibility("quotePolicy.constructionTaxCategory")}
                          value={value.quotePolicy.constructionTaxCategory}
                          onChange={e => onChange({ quotePolicy: { ...value.quotePolicy, constructionTaxCategory: e.target.value } })}
                          className={inputStyles}
                          placeholder="e.g. software"
                        />
                        <ValidationError field="quotePolicy.constructionTaxCategory" message={validationErrors["quotePolicy.constructionTaxCategory"]} />
                      </Field>
                    </HorizontalStack>

                    <div>
                      <div className="mb-3 flex items-center justify-between border-b border-border/40 pb-2">
                        <p className={labelStyles}>Tax Rules</p>
                        <button
                          type="button"
                          onClick={() => {
                            const newRule = {
                              rowId: createRowId("tax"),
                              ruleId: "",
                              name: "",
                              ratePercent: "",
                              categoriesText: "",
                              chargeTypes: [],
                            };
                            onChange({ quotePolicy: { ...value.quotePolicy, taxRules: [...value.quotePolicy.taxRules, newRule] } });
                          }}
                          className="inline-flex items-center gap-1.5 rounded-md border border-border/50 px-3 py-2 text-xs font-bold text-foreground transition-colors hover:bg-muted"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add rule
                        </button>
                      </div>
                      {value.quotePolicy.taxRules.length === 0 ? (
                        <p className="mt-3 rounded-md border border-dashed border-border/40 px-3 py-2 text-[10px] font-medium text-muted-foreground">
                          No tax rules defined.
                        </p>
                      ) : (
                        <VerticalStack gap={4}>
                          {value.quotePolicy.taxRules.map((rule, index) => (
                            <div key={rule.rowId} className="border-t border-border/60 py-4 first:border-t-0">
                              <VerticalStack gap={4}>
                                <HorizontalStack className="items-start justify-between gap-4">
                                  <VerticalStack gap={1} className="min-w-0">
                                    <p className="truncate text-sm font-black text-foreground">{rule.name || "Untitled Tax Rule"}</p>
                                    <DescriptionText className="text-[11px]">{rule.ruleId || "No ID"}</DescriptionText>
                                  </VerticalStack>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      onChange({ quotePolicy: { ...value.quotePolicy, taxRules: value.quotePolicy.taxRules.filter((_, i) => i !== index) } });
                                    }}
                                    className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                    Remove
                                  </button>
                                </HorizontalStack>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                  <Field label="Rule ID" htmlFor={pricebookFieldId(`quotePolicy.taxRules.${index}.ruleId`)}>
                                    <input
                                      {...fieldAccessibility(`quotePolicy.taxRules.${index}.ruleId`)}
                                      value={rule.ruleId}
                                      onChange={e => {
                                        const newTaxRules = [...value.quotePolicy.taxRules];
                                        newTaxRules[index] = { ...newTaxRules[index], ruleId: e.target.value };
                                        onChange({ quotePolicy: { ...value.quotePolicy, taxRules: newTaxRules } });
                                      }}
                                      className={inputStyles}
                                      placeholder="e.g. tax_rule_id_1"
                                    />
                                    <ValidationError field={`quotePolicy.taxRules.${index}.ruleId`} message={validationErrors[`quotePolicy.taxRules.${index}.ruleId`]} />
                                  </Field>
                                  <Field label="Name" htmlFor={pricebookFieldId(`quotePolicy.taxRules.${index}.name`)}>
                                    <input
                                      {...fieldAccessibility(`quotePolicy.taxRules.${index}.name`)}
                                      value={rule.name}
                                      onChange={e => {
                                        const newTaxRules = [...value.quotePolicy.taxRules];
                                        newTaxRules[index] = { ...newTaxRules[index], name: e.target.value };
                                        onChange({ quotePolicy: { ...value.quotePolicy, taxRules: newTaxRules } });
                                      }}
                                      className={inputStyles}
                                      placeholder="e.g. Sales Tax - State A"
                                    />
                                    <ValidationError field={`quotePolicy.taxRules.${index}.name`} message={validationErrors[`quotePolicy.taxRules.${index}.name`]} />
                                  </Field>
                                  <Field label="Rate (%)" htmlFor={pricebookFieldId(`quotePolicy.taxRules.${index}.ratePercent`)}>
                                    <PercentageInput
                                      {...fieldAccessibility(`quotePolicy.taxRules.${index}.ratePercent`)}
                                      value={rule.ratePercent}
                                      onChange={e => {
                                        const newTaxRules = [...value.quotePolicy.taxRules];
                                        newTaxRules[index] = { ...newTaxRules[index], ratePercent: e.target.value };
                                        onChange({ quotePolicy: { ...value.quotePolicy, taxRules: newTaxRules } });
                                      }}
                                      placeholder="8.25"
                                    />
                                    <ValidationError field={`quotePolicy.taxRules.${index}.ratePercent`} message={validationErrors[`quotePolicy.taxRules.${index}.ratePercent`]} />
                                  </Field>
                                  <Field label="Categories (comma-separated)" htmlFor={pricebookFieldId(`quotePolicy.taxRules.${index}.categoriesText`)}>
                                    <input
                                      {...fieldAccessibility(`quotePolicy.taxRules.${index}.categoriesText`)}
                                      value={rule.categoriesText}
                                      onChange={e => {
                                        const newTaxRules = [...value.quotePolicy.taxRules];
                                        newTaxRules[index] = { ...newTaxRules[index], categoriesText: e.target.value };
                                        onChange({ quotePolicy: { ...value.quotePolicy, taxRules: newTaxRules } });
                                      }}
                                      className={inputStyles}
                                      placeholder="e.g. digital_goods, services"
                                    />
                                    <ValidationError field={`quotePolicy.taxRules.${index}.categoriesText`} message={validationErrors[`quotePolicy.taxRules.${index}.categoriesText`]} />
                                  </Field>
                                </div>
                                <div>
                                  <p className={labelStyles}>Charge Types</p>
                                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-2">
                                    {([
                                      { value: "recurring", label: "Recurring" },
                                      { value: "one_time", label: "One-Time" },
                                      { value: "installation", label: "Installation" },
                                      { value: "construction", label: "Construction" },
                                      { value: "deposit", label: "Deposit" },
                                      { value: "usage", label: "Usage" },
                                    ] as const).map(chargeType => (
                                      <div key={chargeType.value} className="flex items-center">
                                        <input
                                          type="checkbox"
                                          id={pricebookFieldId(`quotePolicy.taxRules.${index}.chargeTypes.${chargeType.value}`)}
                                          checked={rule.chargeTypes.includes(chargeType.value)}
                                          onChange={e => {
                                            const newTaxRules = [...value.quotePolicy.taxRules];
                                            const currentChargeTypes = newTaxRules[index].chargeTypes;
                                            const nextChargeTypes = e.target.checked
                                              ? [...currentChargeTypes, chargeType.value]
                                              : currentChargeTypes.filter(type => type !== chargeType.value);
                                            newTaxRules[index] = { ...newTaxRules[index], chargeTypes: nextChargeTypes };
                                            onChange({ quotePolicy: { ...value.quotePolicy, taxRules: newTaxRules } });
                                          }}
                                          className="h-4 w-4 text-primary focus:ring-primary border-border/60"
                                        />
                                        <label htmlFor={pricebookFieldId(`quotePolicy.taxRules.${index}.chargeTypes.${chargeType.value}`)} className="ml-2 text-sm font-medium text-foreground">
                                          {chargeType.label}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                  <ValidationError field={`quotePolicy.taxRules.${index}.chargeTypes`} message={validationErrors[`quotePolicy.taxRules.${index}.chargeTypes`]} />
                                </div>
                              </VerticalStack>
                            </div>
                          ))}
                        </VerticalStack>
                      )}
                    </div>

                  </VerticalStack>
                </div>
              </div>
            </div>
          </section>
        </VerticalStack>
      )}
        </>
      )}
      </main>
    </div>
  );
}

// --- Utilities ---

const formatLeasePreview = (row: PricebookOfferPriceRowViewModel) => {
  const price = row.leasePricing?.intendedSalePriceAmount;
  const rate = row.leasePricing?.leaseRateBps;
  if (price == null && rate == null) {
    return "Enter an intended sale price and interest rate";
  }
  const currency = row.leasePricing?.intendedSalePriceCurrency || "USD";
  const priceLabel =
    price == null
      ? null
      : `${Number(price).toLocaleString(undefined, {
          minimumFractionDigits: 2,
        })} ${currency} intended sale basis`;
  const rateLabel = rate == null ? null : `${formatBasisPointsAsPercent(rate)}% APR`;
  return [priceLabel, rateLabel].filter(Boolean).join(" · ");
};

const formatPricePreview = (
  row: PricebookOfferPriceRowViewModel | PricebookFeePriceRowViewModel,
  baseCurrency?: string | null,
) => {
  const currency = row.currency || baseCurrency || "USD";
  if (row.chargeType === "percentage") return `${row.percentage || "0"}% of reference`;
  if (row.chargeType === "included") return "No Charge";
  
  const amount = parseFloat(row.amount || "0").toLocaleString(undefined, { minimumFractionDigits: 2 });
  const period = (row as any).billingPeriod ? ` / ${(row as any).billingPeriod}` : "";
  return `${amount} ${currency}${period}`;
}
